import React, { useState, useEffect } from 'react';
import { Activity, ChevronRight, User, BookOpen, AlertTriangle, ChevronDown, Check, X } from 'lucide-react';
import { normalizeIPMATCategory, displayIPMATCategory } from '../utils/ipmatCategory';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

/**
 * TestSelectionPage - Page for students to select tests and access AI Insights
 * 
 * Features:
 * - List all available IPMAT tests
 * - Show AI Insights button per test
 * - Check if percentile data is available (gating)
 * - Check if student category is captured
 * - Show proctoring window status
 */

const CATEGORY_OPTIONS = [
  { value: 'GENERAL', label: 'General (UR)' },
  { value: 'NC_OBC', label: 'NC-OBC' },
  { value: 'EWS', label: 'EWS' },
  { value: 'SC', label: 'SC' },
  { value: 'ST', label: 'ST' },
  { value: 'PWD', label: 'PWD (Persons with Disabilities)' },
];

const TestSelectionPage = ({ studentPin, studentName, category, ipmatTests: initialIpmatTests, onSelectIPMATTest, onLogout, jwtToken }) => {
  // Debug: Log received props
  console.log('🔍 TestSelectionPage mounted with props:', { studentPin, studentName, category, hasCategoryProp: category !== undefined });
  
  const [ipmatTests, setIpmatTests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [studentCategory, setStudentCategory] = useState(normalizeIPMATCategory(category));
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [selectedTestId, setSelectedTestId] = useState(null);
  const [categoryInput, setCategoryInput] = useState('');
  const [modalStep, setModalStep] = useState(1); // 1 = select, 2 = confirm
  const [isSaving, setIsSaving] = useState(false);

  // Use passed tests or fetch on mount
  useEffect(() => {
    // Transform IPMAT tests — use testId as primary ID, keep assetId separate
    if (initialIpmatTests && initialIpmatTests.length > 0) {
      const transformedIpmat = initialIpmatTests.map(test => ({
        id: test.testId || test.id,
        testId: test.testId || test.id,
        assetId: test.assetId,
        name: test.testName || test.name,
        date: test.attemptDate || test.date,
        status: 'completed',
        percentileAvailable: true,
        proctoringWindowClose: null,
      }));
      setIpmatTests(transformedIpmat);
    }
    setLoading(false);

    if (!category) fetchStudentCategory();
  }, [studentPin, initialIpmatTests, category]);

  const fetchStudentCategory = async () => {
    try {
      console.log(`📡 Fetching category for student: ${studentPin}`);
      
      // Add timeout to prevent hanging
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000); // 5 second timeout
      
      // Add timestamp to bust browser cache
      const timestamp = Date.now();
      const categoryEndpoint = `${API_BASE_URL}/api/ipmat/student/${studentPin}/category?_=${timestamp}`;
      
      const response = await fetch(categoryEndpoint, {
        signal: controller.signal,
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0',
          ...(jwtToken && { 'Authorization': `Bearer ${jwtToken}` })
        }
      });
      clearTimeout(timeoutId);
      
      const data = await response.json();
      
      console.log('📡 Category API response:', data);
      
      // Parse both CAT and IPMAT response shapes
      const savedCategory = data?.data?.category ?? data?.category ?? null;
      const source = data?.data?.categorySource ?? data?.categorySource ?? null;
      
      if (savedCategory && source !== 'DEFAULT') {
        console.log(`✅ Category found: ${savedCategory} (source: ${source})`);
        setStudentCategory(normalizeIPMATCategory(savedCategory));
      } else {
        console.log('⚠️ No confirmed category found in DB');
        setStudentCategory(null);
      }
    } catch (error) {
      if (error.name === 'AbortError') {
        console.error('❌ Category fetch timed out');
      } else {
        console.error('❌ Error fetching student category:', error);
      }
      setStudentCategory(null);
    }
  };

  const handleAIInsightsClick = (testId) => {
    console.log(`🖱️ AI Insights clicked for test: ${testId}`);
    console.log(`📊 Current studentCategory: ${studentCategory}`);
    console.log(`📊 Prop category: ${displayIPMATCategory(category)}`);
    
    // Check if category is captured
    if (!studentCategory && !category) {
      console.log('⚠️ No category found - showing modal');
      setSelectedTestId(testId);
      setCategoryInput('');
      setModalStep(1);
      setShowCategoryModal(true);
      return;
    }

    // All checks passed - navigate to report card
    console.log(`✅ Category exists (${studentCategory || category}) - navigating to report card`);
    const selected = ipmatTests.find(test => String(test.testId) === String(testId));
    onSelectIPMATTest({
      testId: String(testId),
      assetId: selected?.assetId || '',
      testName: selected?.name || '',
      testDate: selected?.date || '',
      category: normalizeIPMATCategory(studentCategory || category),
    });
  };

  const handleNextStep = () => {
    if (!categoryInput) return;
    setModalStep(2); // Go to confirmation step
  };

  const handleBackStep = () => {
    setModalStep(1); // Go back to selection step
  };

  const handleConfirmCategory = async () => {
    if (!categoryInput) return;

    setIsSaving(true);
    try {
      // Get JWT token from localStorage
      const token = jwtToken || localStorage.getItem('jwtToken');
      
      // Save category to database — IPMAT endpoint
      const saveEndpoint = `${API_BASE_URL}/api/ipmat/student/${studentPin}/category`;
      
      const response = await fetch(saveEndpoint, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` })
        },
        body: JSON.stringify({ category: categoryInput })
      });

      const data = await response.json();
      
      if (data.success) {
        const normalized = normalizeIPMATCategory(data.category || categoryInput);
        setStudentCategory(normalized);
        setShowCategoryModal(false);
        
        // Now proceed to report card
        const selected = ipmatTests.find(test => String(test.testId) === String(selectedTestId));
        onSelectIPMATTest({
          testId: String(selectedTestId),
          assetId: selected?.assetId || '',
          testName: selected?.name || '',
          testDate: selected?.date || '',
          category: normalized,
        });
      } else {
        console.error('Failed to save category:', data.message);
        alert(`Failed to save category: ${data.message}`);
      }
    } catch (error) {
      console.error('Error saving category:', error);
      alert(`Error saving category: ${error.message}`);
    } finally {
      setIsSaving(false);
    }
  };

  const closeModal = () => {
    setShowCategoryModal(false);
    setCategoryInput('');
    setModalStep(1);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  };

  const getStatusBadge = (test) => {
    return (
      <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
        <Activity className="w-3 h-3" />
        Analysis Ready
      </span>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Activity className="w-12 h-12 text-ims-primary animate-pulse mx-auto mb-4" />
          <p className="text-ims-text-secondary">Loading your tests...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <div className="flex items-center gap-3">
              <img 
                src="https://www.imsindia.com/ims-india/wp-content/themes/ims-india-new/img/icons/logo.svg" 
                alt="IMS India" 
                className="h-8"
              />
              <span className="font-semibold text-ims-text-primary hidden sm:block">AI Analytics</span>
            </div>
            
            {/* Profile Section */}
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-3 bg-gray-50 rounded-full pl-1 pr-4 py-1">
                <div className="w-8 h-8 rounded-full bg-ims-primary flex items-center justify-center">
                  <User className="w-4 h-4 text-white" />
                </div>
                <div className="hidden sm:block">
                  <p className="text-sm font-medium text-ims-text-primary">{studentName}</p>
                  <p className="text-xs text-ims-text-muted">{studentPin}</p>
                </div>
                {category && (
                  <span className="text-xs bg-ims-accent/20 text-ims-primary px-2 py-0.5 rounded-full">
                    {displayIPMATCategory(category)}
                  </span>
                )}
              </div>
              
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Page Title */}
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-ims-text-primary mb-2">
            Your IPMAT Tests
          </h1>
          <p className="text-ims-text-secondary">
            Select an IPMAT test to view your AI-powered performance analysis
          </p>
        </div>

        {/* Tests Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {ipmatTests.map((test) => (
            <div
              key={test.id}
              className="bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow overflow-hidden"
            >
              {/* Test Header */}
              <div className="p-6 border-b border-gray-100">
                <div className="flex items-start justify-between mb-4">
                  <div className="w-12 h-12 rounded-lg bg-ims-primary-light flex items-center justify-center">
                    <BookOpen className="w-6 h-6 text-ims-primary" />
                  </div>
                  {getStatusBadge(test)}
                </div>
                <h3 className="text-lg font-semibold text-ims-text-primary mb-1">
                  {test.name}
                </h3>
                <p className="text-sm text-ims-text-muted">
                  {formatDate(test.date)}
                </p>
              </div>

              {/* Test Actions */}
              <div className="p-6">
                <button
                  onClick={() => handleAIInsightsClick(test.id)}
                  className="w-full py-2.5 px-4 bg-ims-primary text-white rounded-lg font-medium hover:bg-ims-primary-dark active:bg-ims-primary-darker transition-colors flex items-center justify-center gap-2"
                >
                  <Activity className="w-4 h-4" />
                  AI Insights
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      </main>

      {/* Category Selection Modal - New Design */}
      {showCategoryModal && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center p-6"
          style={{ backgroundColor: 'rgba(15, 31, 51, 0.45)' }}
          onClick={(e) => e.target === e.currentTarget && closeModal()}
        >
          <div className="bg-white w-full max-w-[400px] rounded-2xl shadow-xl overflow-hidden animate-in fade-in zoom-in duration-200">
            
            {/* Step 1: Category Selection */}
            {modalStep === 1 && (
              <>
                {/* Header */}
                <div className="px-6 pt-5 pb-4 border-b border-gray-100">
                  <div className="flex items-start justify-between">
                    <div>
                      {/* Step dots */}
                      <div className="flex gap-1.5 mb-3">
                        <div className="w-1.5 h-1.5 rounded-full bg-[#1E3A5F]"></div>
                        <div className="w-1.5 h-1.5 rounded-full bg-gray-200"></div>
                      </div>
                      <h2 className="text-base font-semibold text-gray-800">
                        What's your reservation category?
                      </h2>
                      <p className="text-[13px] text-gray-500 mt-1 leading-relaxed">
                        Your category sets the cutoff benchmarks used across all your insights — IIM readiness, section analysis, and action plans.
                      </p>
                    </div>
                    <button 
                      onClick={closeModal}
                      className="p-1 hover:bg-gray-100 rounded-md transition-colors -mt-1 -mr-1"
                    >
                      <X className="w-4 h-4 text-gray-400" />
                    </button>
                  </div>
                </div>

                {/* Body */}
                <div className="px-6 py-5">
                  {/* Dropdown */}
                  <div className="relative">
                    <select
                      value={categoryInput}
                      onChange={(e) => setCategoryInput(e.target.value)}
                      className="w-full px-3 py-2.5 pr-10 border border-gray-200 rounded-xl bg-white text-sm text-gray-800 outline-none transition-all focus:border-[#1E3A5F] focus:ring-2 focus:ring-[#1E3A5F]/10 appearance-none"
                    >
                      <option value="" disabled>Choose your category</option>
                      {CATEGORY_OPTIONS.map((cat) => (
                        <option key={cat.value} value={cat.value}>
                          {cat.label}
                        </option>
                      ))}
                    </select>
                    <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                  </div>

                  {/* Warning */}
                  <div className="mt-3.5 flex items-start gap-2 px-3 py-2.5 bg-amber-50 border border-amber-200 rounded-lg">
                    <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                    <p className="text-xs text-amber-800 leading-relaxed">
                      <span className="font-semibold">Choose carefully.</span> This is a one-time setting and cannot be changed after confirmation.
                    </p>
                  </div>
                </div>

                {/* Footer */}
                <div className="flex gap-2 px-6 py-4 border-t border-gray-100">
                  <button
                    onClick={closeModal}
                    className="px-5 py-2.5 border border-gray-200 text-gray-500 rounded-xl text-sm font-medium hover:bg-gray-50 hover:text-gray-700 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleNextStep}
                    disabled={!categoryInput}
                    className="flex-1 py-2.5 px-4 bg-[#1E3A5F] text-white rounded-xl text-sm font-medium hover:bg-[#2D5A8E] disabled:opacity-45 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-1.5"
                  >
                    Continue
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </>
            )}

            {/* Step 2: Confirmation */}
            {modalStep === 2 && (
              <>
                {/* Header */}
                <div className="px-6 pt-5 pb-4 border-b border-gray-100">
                  <div className="flex items-start justify-between">
                    <div>
                      {/* Step dots */}
                      <div className="flex gap-1.5 mb-3">
                        <div className="w-1.5 h-1.5 rounded-full bg-gray-200"></div>
                        <div className="w-1.5 h-1.5 rounded-full bg-[#1E3A5F]"></div>
                      </div>
                      <h2 className="text-base font-semibold text-gray-800">
                        Confirm your selection
                      </h2>
                      <p className="text-[13px] text-gray-500 mt-1 leading-relaxed">
                        Once confirmed, your category is locked in. All cutoff data and benchmarks will be calibrated to it.
                      </p>
                    </div>
                    <button 
                      onClick={closeModal}
                      className="p-1 hover:bg-gray-100 rounded-md transition-colors -mt-1 -mr-1"
                    >
                      <X className="w-4 h-4 text-gray-400" />
                    </button>
                  </div>
                </div>

                {/* Body */}
                <div className="px-6 py-5">
                  {/* Selected category pill */}
                  <div className="text-center mb-5">
                    <p className="text-xs text-gray-400 mb-2.5 uppercase tracking-wider font-semibold">Selected category</p>
                    <div className="inline-flex items-center px-5 py-2.5 bg-blue-50 border border-blue-100 rounded-xl">
                      <span className="text-[15px] font-semibold text-[#1E3A5F]">
                        {CATEGORY_OPTIONS.find(c => c.value === categoryInput)?.label || categoryInput}
                      </span>
                    </div>
                  </div>

                  {/* Warning */}
                  <div className="bg-red-50 border border-red-100 rounded-xl p-4">
                    <p className="text-sm text-red-800 font-semibold mb-1">This cannot be changed later.</p>
                    <p className="text-xs text-red-700/85 leading-relaxed">
                      Your IIM readiness benchmarks, section cutoffs, and all insights will be permanently tied to this category. Make sure it matches your actual reservation status.
                    </p>
                  </div>
                </div>

                {/* Footer */}
                <div className="flex gap-2 px-6 py-4 border-t border-gray-100">
                  <button
                    onClick={handleBackStep}
                    disabled={isSaving}
                    className="flex-1 py-2.5 px-4 border border-gray-200 text-gray-500 rounded-xl text-sm font-medium hover:bg-gray-50 hover:text-gray-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-1"
                  >
                    <ChevronRight className="w-4 h-4 rotate-180" />
                    Back
                  </button>
                  <button
                    onClick={handleConfirmCategory}
                    disabled={isSaving}
                    className="flex-1 py-2.5 px-4 bg-[#1E3A5F] text-white rounded-xl text-sm font-medium hover:bg-[#2D5A8E] disabled:opacity-45 transition-colors"
                  >
                    {isSaving ? 'Saving...' : 'Confirm category'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default TestSelectionPage;
